// Runtime live-config validation + secret refusal for the SecOps console.
//
// The host injects `window.__SECOPS_LIVE_CONFIG__` BEFORE the app bundle runs (in dev via
// the gitignored public/live-config.local.js; in production via the host). The publishable
// key is PUBLIC (anon-style) but is supplied only at RUNTIME and is never committed.
//
// This module is the CONFIG GATE. It validates the injected shape and, CRITICALLY, REFUSES a
// key that looks like a service-role / secret / JWT / bearer / raw chk_ token / 64-hex hash —
// only a publishable/anon-style key is accepted. The refusal `reason` is always SAFE: it never
// echoes the key value. A refused config can never be turned into a live supabase client, so a
// secret can never reach the browser data path.
//
// Ported from apps/web/src/live-config.ts (the pilot console), minus the hand-rolled fetch
// reader wiring — the console builds a real supabase-js client from the validated config in
// ./supabase-client.ts. Kept deliberately dependency-free so it is trivially unit-testable.

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DEFAULT_REFRESH_MS = 30000;
const DEFAULT_DISPLAY_TIME_ZONE = "America/New_York";

/** Supported basemap styles for the optional MapTiler basemap. Unknown/absent => "hybrid". */
export type MapStyle = "streets" | "satellite" | "hybrid";

const MAP_STYLES: readonly MapStyle[] = ["streets", "satellite", "hybrid"];
const DEFAULT_MAP_STYLE: MapStyle = "hybrid";

/** The validated, browser-safe runtime config. accountId is normalized to lowercase. */
export interface LiveConfig {
  supabaseUrl: string;
  supabasePublishableKey: string;
  accountId: string;
  accountName: string;
  refreshMs: number;
  /**
   * Optional MapTiler PUBLISHABLE client token. Absent (or secret-shaped, which is dropped)
   * means the live map keeps a privacy-safe blank local grid with ZERO third-party calls.
   * Supplied at RUNTIME only; never committed. Dropped (silently) if it looks secret-shaped.
   */
  mapTilerKey?: string;
  /** Basemap style. Defaults to "hybrid"; an unknown value also normalizes to "hybrid". */
  mapStyle: MapStyle;
  /**
   * Optional TomTom PUBLISHABLE browser key for live flow tiles. It has the same runtime-only,
   * secret-refusal posture as mapTilerKey. Absent or refused means no TomTom request is possible.
   */
  tomTomKey?: string;
  /** Optional workspace dark switch. Only an explicit false disables otherwise valid traffic. */
  trafficEnabled?: boolean;
  /** Optional operator default. The locked default is false when this field is absent. */
  trafficEnabledDefault?: boolean;
  /**
   * IANA timezone all displayed timestamps render in (display-only; stored values stay UTC).
   * Defaults to "America/New_York" (Eastern; DST-aware). A non-IANA value falls back to default.
   */
  displayTimeZone: string;
  /**
   * Optional PostHog project key (publishable phc_… — browser-class, but runtime-injected only,
   * never committed) + ingest host. Both absent = analytics fully off (no SDK load). Unlike
   * mapTilerKey, a present-but-invalid value fails the WHOLE config loudly: a misconfigured
   * analytics key is a deploy error, not a cosmetic downgrade.
   */
  posthogKey?: string;
  posthogHost?: string;
  /**
   * Optional Sentry browser DSN (public client key). Absent = error reporting off (no SDK load).
   * Same deploy posture as PostHog: runtime-injected only, never committed. A present-but-invalid
   * value fails the whole config loudly so a bad DSN cannot half-enable monitoring.
   */
  sentryDsn?: string;
  /**
   * Optional Sentry environment tag (e.g. staging, production). Defaults to "production" when DSN
   * is set and this field is absent. Must be a short safe tag (no free text that could leak secrets).
   */
  sentryEnvironment?: string;
}

/** Discriminated result; on failure `reason` is SAFE (never the key value). GW-CONFIG-GATE
 *  port: `problems` carries EVERY missing/invalid key message (names only) so the boot screen
 *  shows operators the complete fix-list in one look instead of one error per deploy attempt. */
export type ParseLiveConfigResult =
  | { ok: true; config: LiveConfig }
  | { ok: false; reason: string; problems?: string[] };

/**
 * Validate `window.__SECOPS_LIVE_CONFIG__`.
 *
 * Returns { ok: true, config } only when every field is valid AND the publishable key does
 * not look like a service-role / secret / JWT / bearer / raw chk_ token / 64-hex hash. The
 * failure `reason` is a fixed, safe sentence — it never includes the key value.
 */
export function parseLiveConfig(raw: unknown): ParseLiveConfigResult {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    // Nothing injected at all (the live-config script never ran) - still give operators the
    // COMPLETE required-key list by validating an empty object, headline first.
    const empty = parseLiveConfig({});
    const detail = empty.ok ? [] : (empty.problems ?? []);
    return {
      ok: false,
      reason: "Live config is missing or not an object.",
      problems: ["Live config is missing or not an object.", ...detail],
    };
  }
  const source = raw as Record<string, unknown>;
  const problems: string[] = [];

  const url = parseHttpsUrl(source.supabaseUrl);
  if (url === null) {
    problems.push("supabaseUrl must be an https:// URL.");
  }

  const rawKey = source.supabasePublishableKey;
  let key: string | null = null;
  if (typeof rawKey !== "string" || rawKey.trim().length === 0) {
    problems.push("supabasePublishableKey must be a non-empty string.");
  } else if (looksLikeSecret(rawKey.trim())) {
    // SAFE reason - describe the rule, never the value.
    problems.push(
      "supabasePublishableKey was refused: it looks like a service-role/secret/JWT/bearer/raw-token value. Only a publishable (anon) key may be used in the browser.",
    );
  } else {
    key = rawKey.trim();
  }

  let accountId: string | null = null;
  if (typeof source.accountId !== "string" || !UUID_PATTERN.test(source.accountId.trim())) {
    problems.push("accountId must be a UUID.");
  } else {
    accountId = source.accountId.trim().toLowerCase();
  }

  let accountName: string | null = null;
  if (typeof source.accountName !== "string" || source.accountName.trim().length === 0) {
    problems.push("accountName must be a non-empty string.");
  } else {
    accountName = source.accountName.trim();
  }

  const refreshMs = parseRefreshMs(source.refreshMs);
  if (refreshMs === null) {
    problems.push("refreshMs must be a positive integer (milliseconds).");
  }

  // Browser map keys are OPTIONAL. A trimmed non-empty non-secret string is kept;
  // absent/blank/non-string is omitted; a SECRET-shaped value is DROPPED (not a hard failure)
  // so the Console and its first-party desks keep working.
  const mapTilerKey = parseOptionalPublishableKey(source.mapTilerKey);
  const tomTomKey = parseOptionalPublishableKey(source.tomTomKey);
  const mapStyle = parseMapStyle(source.mapStyle);
  const displayTimeZone = resolveDisplayTimeZone(
    typeof source.displayTimeZone === "string" ? source.displayTimeZone : undefined,
  );

  const posthog = parsePosthogFields(source.posthogKey, source.posthogHost);
  if (!posthog.ok) {
    problems.push(posthog.reason);
  }

  const sentry = parseSentryFields(source.sentryDsn, source.sentryEnvironment);
  if (!sentry.ok) {
    problems.push(sentry.reason);
  }

  if (
    problems.length > 0 ||
    url === null ||
    key === null ||
    accountId === null ||
    accountName === null ||
    refreshMs === null ||
    !posthog.ok ||
    !sentry.ok
  ) {
    return { ok: false, reason: problems.join(" "), problems };
  }

  const config: LiveConfig = {
    supabaseUrl: url.toString().replace(/\/$/, ""),
    supabasePublishableKey: key,
    accountId,
    accountName,
    refreshMs,
    mapStyle,
    displayTimeZone,
  };
  if (mapTilerKey !== undefined) {
    config.mapTilerKey = mapTilerKey;
  }
  if (tomTomKey !== undefined) {
    config.tomTomKey = tomTomKey;
  }
  if (source.trafficEnabled !== undefined) {
    // Unknown values fail dark without taking down the Console. Only literal true enables.
    config.trafficEnabled = source.trafficEnabled === true;
  }
  if (source.trafficEnabledDefault !== undefined) {
    // Unknown values normalize to the locked operator default, which is off.
    config.trafficEnabledDefault = source.trafficEnabledDefault === true;
  }
  if (posthog.key !== undefined && posthog.host !== undefined) {
    config.posthogKey = posthog.key;
    config.posthogHost = posthog.host;
  }
  if (sentry.dsn !== undefined && sentry.environment !== undefined) {
    config.sentryDsn = sentry.dsn;
    config.sentryEnvironment = sentry.environment;
  }
  return { ok: true, config };
}

const POSTHOG_KEY_PATTERN = /^phc_[A-Za-z0-9_-]{16,}$/;
const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";

/** Validate the optional analytics fields. Same posture as the supabase key: a present-but-wrong
 *  value fails loudly with a SAFE reason; only a phc_ publishable project key is accepted. */
function parsePosthogFields(
  rawKey: unknown,
  rawHost: unknown,
): { ok: true; key?: string; host?: string } | { ok: false; reason: string } {
  if (rawKey === undefined || rawKey === null) {
    // A host without a key configures nothing - ignore it rather than half-enable analytics.
    return { ok: true };
  }
  if (typeof rawKey !== "string" || rawKey.trim().length === 0) {
    return { ok: false, reason: "posthogKey must be a non-empty string when present." };
  }
  const key = rawKey.trim();
  if (looksLikeSecret(key) || !POSTHOG_KEY_PATTERN.test(key)) {
    return {
      ok: false,
      reason: "posthogKey was refused: only a publishable phc_ project key may be used in the browser.",
    };
  }
  if (rawHost === undefined || rawHost === null) {
    return { ok: true, key, host: DEFAULT_POSTHOG_HOST };
  }
  const host = parseHttpsUrl(rawHost);
  if (host === null) {
    return { ok: false, reason: "posthogHost must be an https:// URL when present." };
  }
  return { ok: true, key, host: host.origin };
}

const DEFAULT_SENTRY_ENVIRONMENT = "production";
const SENTRY_ENV_PATTERN = /^[a-z][a-z0-9_-]{0,31}$/;

/** Optional Sentry browser DSN + environment. Absent DSN = off. Present-but-wrong = loud fail. */
function parseSentryFields(
  rawDsn: unknown,
  rawEnvironment: unknown,
): { ok: true; dsn?: string; environment?: string } | { ok: false; reason: string } {
  if (rawDsn === undefined || rawDsn === null) {
    return { ok: true };
  }
  if (typeof rawDsn !== "string" || rawDsn.trim().length === 0) {
    return { ok: false, reason: "sentryDsn must be a non-empty string when present." };
  }
  const dsn = rawDsn.trim();
  if (looksLikeSecret(dsn) || !isBrowserSentryDsn(dsn)) {
    return {
      ok: false,
      reason:
        "sentryDsn was refused: only a public https Sentry browser DSN (ingest.sentry.io project) may be used in the browser.",
    };
  }
  if (rawEnvironment === undefined || rawEnvironment === null) {
    return { ok: true, dsn, environment: DEFAULT_SENTRY_ENVIRONMENT };
  }
  if (typeof rawEnvironment !== "string" || rawEnvironment.trim().length === 0) {
    return { ok: false, reason: "sentryEnvironment must be a non-empty string when present." };
  }
  const environment = rawEnvironment.trim().toLowerCase();
  if (!SENTRY_ENV_PATTERN.test(environment)) {
    return {
      ok: false,
      reason: "sentryEnvironment was refused: use a short tag like staging or production.",
    };
  }
  return { ok: true, dsn, environment };
}

/**
 * True when `value` looks like a public browser Sentry DSN (not a service-role / JWT).
 * Accepts US/DE/EU ingest hosts and the default ingest.sentry.io form.
 */
export function isBrowserSentryDsn(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol !== "https:") {
    return false;
  }
  if (url.username.length < 8 || url.password.length > 0) {
    return false;
  }
  if (!/^\/\d+\/?$/.test(url.pathname)) {
    return false;
  }
  // o123.ingest.sentry.io | o123.ingest.us.sentry.io | o123.ingest.de.sentry.io | …
  return /(?:^|\.)ingest(?:\.(?:us|de|eu))?\.sentry\.io$/i.test(url.hostname);
}

/** Read + validate the host-injected runtime config from `window`. */
export function readRuntimeConfig(): ParseLiveConfigResult {
  if (typeof window === "undefined") {
    return fail("Runtime config is unavailable (no window).");
  }
  return parseLiveConfig(window.__SECOPS_LIVE_CONFIG__);
}

/**
 * Returns true when a candidate key looks like a SECRET rather than a publishable (anon) key.
 * Matches: service_role marker, sb_secret_ prefix, JWT shape (eyJ... with two dots), a bearer
 * prefix, a raw chk_ checkpoint token, or a bare 64-hex hash.
 *
 * Conservative by design: a false refusal is a deploy-config error (safe), whereas a false
 * accept would put a secret in the browser (unsafe). Mirrors the pilot console's gate so the
 * two consoles refuse identically.
 */
export function looksLikeSecret(key: string): boolean {
  const lower = key.toLowerCase();

  if (lower.includes("service_role")) {
    return true;
  }
  if (lower.includes("sb_secret_")) {
    return true;
  }
  if (/^bearer\b/i.test(key) || /\bbearer\s+\S/i.test(key)) {
    return true;
  }
  // JWT: header.payload.signature, header begins with eyJ (base64url of '{"').
  if (/^eyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]+$/.test(key)) {
    return true;
  }
  if (/(^|[^A-Za-z0-9_-])eyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]+/.test(key)) {
    return true;
  }
  // Raw checkpoint token: chk_ prefix.
  if (/^chk_/i.test(key) || /\bchk_[A-Za-z0-9]/i.test(key)) {
    return true;
  }
  // Bare 64-hex hash (e.g. a token hash): a 64-hex run with no other hex adjacent.
  if (/(^|[^0-9a-f])[0-9a-f]{64}([^0-9a-f]|$)/i.test(key)) {
    return true;
  }
  return false;
}

/** True when `tz` is a valid IANA timezone (constructing the formatter throws otherwise). */
export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Resolve a display timezone: a valid IANA value is kept; anything else => America/New_York. */
export function resolveDisplayTimeZone(value: string | undefined): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length > 0 && isValidTimeZone(trimmed)) {
      return trimmed;
    }
  }
  return DEFAULT_DISPLAY_TIME_ZONE;
}

function parseHttpsUrl(value: unknown): URL | null {
  if (typeof value !== "string" || value.trim().length === 0) {
    return null;
  }
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return null;
  }
  return url.protocol === "https:" ? url : null;
}

function parseRefreshMs(value: unknown): number | null {
  if (value === undefined) {
    return DEFAULT_REFRESH_MS;
  }
  if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) {
    return null;
  }
  return value;
}

function parseOptionalPublishableKey(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return undefined;
  }
  if (looksLikeSecret(trimmed)) {
    return undefined;
  }
  return trimmed;
}

function parseMapStyle(value: unknown): MapStyle {
  if (typeof value === "string") {
    const trimmed = value.trim().toLowerCase();
    if ((MAP_STYLES as readonly string[]).includes(trimmed)) {
      return trimmed as MapStyle;
    }
  }
  return DEFAULT_MAP_STYLE;
}

function fail(reason: string): { ok: false; reason: string } {
  return { ok: false, reason };
}
